import {
  Camera,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  KeyRound,
  MapPin,
  Star,
  Users,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { JOB_STATUS_TONES, useJob, useSetTaskCompletion } from '@/hooks/jobs';
import { ApiError } from '@/lib/api';
import { formatDay, formatDuration, formatInstant, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  AssignmentDialog,
  CancelJobDialog,
  CompleteJobDialog,
  EditJobDialog,
  RescheduleDialog,
} from '@/pages/admin/jobs/JobActionDialogs';
import { PhotoUploadDialog } from '@/pages/admin/jobs/PhotoUploadDialog';
import type { JobDetail } from '@shared/schemas/job';
import { useStartJob } from '@/hooks/jobs';

/*
 * /admin/jobs/:id — ROADMAP.md §4.4.
 *
 * The checklist section is the important one. A task that requires a photo
 * has **no tick control here** — its completion is the photo, uploaded from
 * the field (business-rules.md §3.1). The office can tick the tasks that
 * don't require one, and the counts banner says plainly what stands between
 * the job and completion.
 *
 * Time logged is `started_at` → `completed_at` for now; per-person clock time
 * arrives with `time_log` in phase 6.
 */

type DialogName = 'assign' | 'reschedule' | 'cancel' | 'complete' | 'edit' | 'photo' | null;

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const job = useJob(id);
  const [dialog, setDialog] = useState<DialogName>(null);

  if (job.isError) {
    return (
      <AdminPage title="Job">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="That job could not be loaded"
            onRetry={() => {
              void job.refetch();
            }}
          />
        </div>
      </AdminPage>
    );
  }

  if (job.isPending) {
    return (
      <AdminPage title="Job">
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading the job</span>
          <div aria-hidden="true" className="h-32 animate-pulse rounded-lg bg-white" />
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-white" />
        </div>
      </AdminPage>
    );
  }

  const detail = job.data;

  return (
    <AdminPage
      title={detail.jobNumber}
      record={detail.jobNumber}
      description={`${detail.serviceTypeName} for ${detail.clientName}`}
      actions={<HeaderActions detail={detail} openDialog={setDialog} />}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge label={detail.statusLabel} tone={JOB_STATUS_TONES[detail.status]} />
        {detail.isHighPriority ? (
          <span className="inline-flex items-center gap-1 text-meta text-ink-700">
            <Star aria-hidden="true" className="size-4 fill-honey-400 text-status-pending-text" />
            High priority
          </span>
        ) : null}
        {detail.status === 'cancelled' && detail.cancelledReason ? (
          <span className="text-meta text-ink-700">— {detail.cancelledReason}</span>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <ChecklistCard detail={detail} />
          <PhotosCard
            detail={detail}
            onAdd={() => {
              setDialog('photo');
            }}
          />
          <HistoryCard detail={detail} />
        </div>

        <div className="space-y-4">
          <ScheduleCard detail={detail} />
          <PropertyCard detail={detail} />
          <CrewCard
            detail={detail}
            onEdit={() => {
              setDialog('assign');
            }}
          />
          {detail.specialInstructions ? (
            <Card title="Special instructions">
              <p className="whitespace-pre-wrap text-body text-ink-900">
                {detail.specialInstructions}
              </p>
            </Card>
          ) : null}
        </div>
      </div>

      <AssignmentDialog
        key={`assign-${String(dialog === 'assign')}`}
        job={detail}
        open={dialog === 'assign'}
        onOpenChange={(next) => {
          setDialog(next ? 'assign' : null);
        }}
      />
      <RescheduleDialog
        key={`reschedule-${String(dialog === 'reschedule')}`}
        job={detail}
        open={dialog === 'reschedule'}
        onOpenChange={(next) => {
          setDialog(next ? 'reschedule' : null);
        }}
      />
      <CancelJobDialog
        key={`cancel-${String(dialog === 'cancel')}`}
        job={detail}
        open={dialog === 'cancel'}
        onOpenChange={(next) => {
          setDialog(next ? 'cancel' : null);
        }}
      />
      <CompleteJobDialog
        key={`complete-${String(dialog === 'complete')}`}
        job={detail}
        open={dialog === 'complete'}
        onOpenChange={(next) => {
          setDialog(next ? 'complete' : null);
        }}
      />
      <EditJobDialog
        key={`edit-${String(dialog === 'edit')}`}
        job={detail}
        open={dialog === 'edit'}
        onOpenChange={(next) => {
          setDialog(next ? 'edit' : null);
        }}
      />
      <PhotoUploadDialog
        key={`photo-${String(dialog === 'photo')}`}
        job={detail}
        open={dialog === 'photo'}
        onOpenChange={(next) => {
          setDialog(next ? 'photo' : null);
        }}
      />
    </AdminPage>
  );
}

/**
 * The actions this status actually has. A completed or cancelled job shows
 * none — the record is closed, and a button that always 409s is a trap.
 */
function HeaderActions({
  detail,
  openDialog,
}: {
  detail: JobDetail;
  openDialog: (name: DialogName) => void;
}) {
  const toast = useToast();
  const start = useStartJob(detail.id);

  if (detail.status === 'completed' || detail.status === 'cancelled') return null;

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        onClick={() => {
          openDialog('edit');
        }}
      >
        Edit
      </Button>
      <Button
        variant="outline"
        onClick={() => {
          openDialog('reschedule');
        }}
      >
        Reschedule
      </Button>
      <Button
        variant="outline"
        onClick={() => {
          openDialog('cancel');
        }}
      >
        Cancel job
      </Button>
      {detail.status === 'scheduled' ? (
        <Button
          disabled={start.isPending}
          onClick={() => {
            start.mutate(undefined, {
              onSuccess: () => {
                toast.success(`${detail.jobNumber} started.`);
              },
              onError: (error) => {
                toast.error(error instanceof ApiError ? error.message : 'Could not start the job.');
              },
            });
          }}
        >
          {start.isPending ? 'Starting…' : 'Start job'}
        </Button>
      ) : null}
      {detail.status === 'in_progress' ? (
        <Button
          onClick={() => {
            openDialog('complete');
          }}
        >
          Complete…
        </Button>
      ) : null}
      {detail.status === 'pending' ? (
        <Button
          onClick={() => {
            openDialog('assign');
          }}
        >
          Assign crew
        </Button>
      ) : null}
    </div>
  );
}

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-h3 text-ink-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ScheduleCard({ detail }: { detail: JobDetail }) {
  return (
    <Card title="Schedule">
      <dl className="space-y-2 text-body">
        <div className="flex justify-between gap-2">
          <dt className="text-ink-500">Date</dt>
          <dd className="text-ink-900">{formatDay(detail.scheduledDate)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-500">Start</dt>
          <dd className="text-ink-900">{formatWallTime(detail.scheduledTime)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-500">Estimated</dt>
          <dd className="text-ink-900">{formatDuration(detail.estimatedDurationMinutes)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-500">Started</dt>
          <dd className="text-ink-900">
            {detail.startedAt ? formatInstant(detail.startedAt) : 'Not yet'}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-ink-500">Completed</dt>
          <dd className="text-ink-900">
            {detail.completedAt ? formatInstant(detail.completedAt) : 'Not yet'}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

function PropertyCard({ detail }: { detail: JobDetail }) {
  const [codesVisible, setCodesVisible] = useState(false);

  return (
    <Card title="Property">
      <p className="flex items-start gap-2 text-body text-ink-900">
        <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
        {detail.addressSnapshot}
      </p>
      <p className="mt-1.5 pl-6 text-meta text-ink-500">
        The address as it was when the job was created — editing the property does not change it.
      </p>

      {detail.propertyAccessNotes ? (
        <div className="mt-3 rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-label text-ink-900">
              <KeyRound aria-hidden="true" className="size-4" />
              Access notes
            </p>
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={codesVisible}
              onClick={() => {
                setCodesVisible((current) => !current);
              }}
            >
              {codesVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              {codesVisible ? 'Hide' : 'Show'}
            </Button>
          </div>
          {codesVisible ? (
            <p className="mt-2 whitespace-pre-wrap text-body text-ink-900">
              {detail.propertyAccessNotes}
            </p>
          ) : (
            <p className="mt-2 text-meta text-ink-500">
              Hidden. A cleaner sees these only for a job they are assigned to.
            </p>
          )}
        </div>
      ) : null}
    </Card>
  );
}

function CrewCard({ detail, onEdit }: { detail: JobDetail; onEdit: () => void }) {
  const editable = detail.status !== 'completed' && detail.status !== 'cancelled';

  return (
    <Card
      title="Crew"
      action={
        editable ? (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Users aria-hidden="true" />
            Change
          </Button>
        ) : null
      }
    >
      {detail.assignees.length === 0 ? (
        <p className="text-body text-ink-500">
          Nobody yet — the job stays pending until someone is assigned.
        </p>
      ) : (
        <ul className="space-y-2">
          {detail.assignees.map((entry) => (
            <li key={entry.employeeId} className="flex items-center justify-between gap-2">
              <span className="text-body text-ink-900">{entry.name}</span>
              <span className="text-meta capitalize text-ink-500">{entry.role}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChecklistCard({ detail }: { detail: JobDetail }) {
  const toast = useToast();
  const completion = useSetTaskCompletion(detail.id);
  const closed = detail.status === 'completed' || detail.status === 'cancelled';

  if (detail.tasks.length === 0) {
    return (
      <Card title="Checklist">
        <p className="text-body text-ink-500">
          This job was created without a checklist. The next one can pick a property category in the
          create dialog.
        </p>
      </Card>
    );
  }

  const { requiredCompleted, requiredTotal } = detail.taskCounts;

  return (
    <Card title={`Checklist${detail.categoryName ? ` — ${detail.categoryName}` : ''}`}>
      {/* The live count, so nobody discovers the photo rule at the end
          (business-rules.md §3.1). */}
      <p className="bg-paper-50 mb-3 rounded-sm px-3 py-2 text-meta text-ink-700">
        {requiredCompleted} of {requiredTotal} required tasks done. Tasks marked{' '}
        <Camera aria-hidden="true" className="inline size-3.5 align-[-2px]" />
        <span className="sr-only">camera</span> are completed by their photo, uploaded from the
        field — they cannot be ticked from the office.
      </p>

      <ul className="divide-y divide-ink-100">
        {detail.tasks.map((task) => (
          <li key={task.id} className="flex items-center gap-3 py-2.5">
            {task.isCompleted ? (
              <CheckCircle2
                aria-hidden="true"
                className="size-5 shrink-0 text-status-completed-text"
              />
            ) : (
              <Circle aria-hidden="true" className="size-5 shrink-0 text-ink-300" />
            )}

            <div className="min-w-0 flex-1">
              <p className="text-body text-ink-900">
                {task.name}
                {!task.isRequired ? (
                  <span className="ml-2 text-meta text-ink-500">Optional</span>
                ) : null}
              </p>
              <p className="text-meta text-ink-500">
                {task.isCompleted
                  ? task.completedByName
                    ? `Done by ${task.completedByName}`
                    : 'Done'
                  : task.requiresPhoto
                    ? 'Waiting for a photo'
                    : 'Open'}
                {task.photoCount > 0
                  ? ` · ${String(task.photoCount)} photo${task.photoCount === 1 ? '' : 's'}`
                  : ''}
              </p>
            </div>

            {task.requiresPhoto ? (
              <span
                title="Completed by its photo"
                className="inline-flex size-11 shrink-0 items-center justify-center text-ink-500"
              >
                <Camera aria-hidden="true" className="size-4" />
                <span className="sr-only">Requires a photo</span>
              </span>
            ) : closed ? null : (
              <Button
                variant="ghost"
                size="sm"
                disabled={completion.isPending}
                onClick={() => {
                  completion.mutate(
                    { taskId: task.id, isCompleted: !task.isCompleted },
                    {
                      onError: (error) => {
                        toast.error(
                          error instanceof ApiError ? error.message : 'Could not save that.',
                        );
                      },
                    },
                  );
                }}
              >
                {task.isCompleted ? 'Reopen' : 'Mark done'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PhotosCard({ detail, onAdd }: { detail: JobDetail; onAdd: () => void }) {
  const closed = detail.status === 'completed' || detail.status === 'cancelled';
  // Photos are welcome on a completed job — evidence arriving late is still
  // evidence — but not on a cancelled one, and never before there is a crew
  // to attribute them to.
  const canAdd = detail.status !== 'cancelled' && detail.assignees.length > 0;
  const addButton = canAdd ? (
    <Button variant="ghost" size="sm" onClick={onAdd}>
      <Camera aria-hidden="true" />
      Add photo
    </Button>
  ) : null;

  if (detail.photos.length === 0) {
    return (
      <Card title="Photos" action={addButton}>
        <p className="text-body text-ink-500">
          {closed
            ? 'No photos were uploaded for this job.'
            : detail.assignees.length === 0
              ? 'No photos yet — and none can be added until the job has a crew to take them.'
              : 'No photos yet. They arrive from the field as the crew works the checklist, or the office can add one the crew sent in.'}
        </p>
      </Card>
    );
  }

  const taskNames = new Map(detail.tasks.map((task) => [task.id, task.name]));

  return (
    <Card title={`Photos (${String(detail.photos.length)})`} action={addButton}>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {detail.photos.map((photo) => (
          <li key={photo.id}>
            <a href={photo.url} target="_blank" rel="noreferrer" className="group block">
              <img
                src={photo.url}
                alt={
                  photo.caption ??
                  (photo.jobTaskId
                    ? `Photo for ${taskNames.get(photo.jobTaskId) ?? 'a task'}`
                    : 'Job photo')
                }
                loading="lazy"
                className="aspect-square w-full rounded-sm border border-ink-300 object-cover group-hover:opacity-90"
              />
            </a>
            <p className="mt-1 truncate text-meta text-ink-500">
              {photo.jobTaskId ? (taskNames.get(photo.jobTaskId) ?? photo.kind) : photo.kind} ·{' '}
              {photo.uploadedByName}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function HistoryCard({ detail }: { detail: JobDetail }) {
  return (
    <Card title="History">
      <ol className="space-y-3">
        {detail.history.map((entry) => (
          <li key={entry.id} className="flex gap-3">
            <span aria-hidden="true" className="mt-1.5 size-2 shrink-0 rounded-full bg-honey-500" />
            <div className="min-w-0">
              <p className="text-body text-ink-900">
                {entry.fromStatusLabel === null
                  ? `Created as ${entry.toStatusLabel.toLowerCase()}`
                  : `${entry.fromStatusLabel} → ${entry.toStatusLabel}`}
              </p>
              <p className="text-meta text-ink-500">
                {formatInstant(entry.changedAt)}
                {entry.changedByName ? ` · ${entry.changedByName}` : ''}
              </p>
              {entry.note ? <p className="mt-0.5 text-meta text-ink-700">{entry.note}</p> : null}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
